import { InformationTypeEnum } from 'src/app/enum/shopping-cart/information-type.enum';
import { ShoppingCartProductInformation } from './shopping-cart-product-information';
import { ShoppingCartProductAccessory } from './shopping-cart-product-accessory';
import { ShoppingCartDiscount } from './shopping-cart-discount';
import {
  getPriceHtFromTtc,
  getPriceTtcFromHtAndTva,
  getPriceWithDiscount,
  getTvaAmountFromPriceTtc,
} from 'src/app/services/helpers/formula-helper';

export interface CompositeDimension {
  label: string;
  shortLabel: string;
  value?: number;
  unit: string;
}

export interface CompositeAreaProduct {
  id: number;
  name: string;
  code: string;
  dimension1: CompositeDimension;
  dimension2: CompositeDimension;
  informations?: string[];
  options?: any[];
}

export interface CompositeArea {
  number: number;
  product: CompositeAreaProduct;
}

export interface CompositeStructure {
  areas: CompositeArea[];
}

export class ShoppingCartProduct {
  id: string;
  externalId: number;
  name: string;
  thumbnail: string;
  productCode: string;
  ean: string;
  quantity: number;
  unitPurchasePriceHt: number;
  unitPriceHt: number;
  discount: number;
  tva: number;
  unitEcoTax: number;
  informations: ShoppingCartProductInformation[];
  composite?: CompositeStructure;
  accessories: ShoppingCartProductAccessory[];
  accessoriesAvailable: boolean;
  orderNumber: string;
  supplierName: string;
  supplierSent: boolean;
  supplierSentLastDate?: Date;
  automaticSupplierSent?: boolean;
  creatorId: string;
  creationTime: Date;
  lastModificationTime: Date;
  availableDiscounts: ShoppingCartDiscount[];
  index?: number;

  constructor(
    id: string,
    externalId: number,
    name: string,
    thumbnail: string,
    productCode: string,
    ean: string,
    quantity: number,
    unitPurchasePriceHt: number,
    unitPriceHt: number,
    discount: number,
    tva: number,
    unitEcoTax: number,
    informations: ShoppingCartProductInformation[],
    accessories: ShoppingCartProductAccessory[],
    orderNumber: string,
    supplierName: string,
    supplierSent: boolean,
    supplierSentLastDate?: Date,
    automaticSupplierSent?: boolean,
    composite?: CompositeStructure,
    index?: number
  ) {
    this.id = id;
    this.externalId = externalId;
    this.name = name;
    this.thumbnail = thumbnail;
    this.productCode = productCode;
    this.ean = ean;
    this.quantity = quantity;
    this.unitPurchasePriceHt = unitPurchasePriceHt;
    this.unitPriceHt = unitPriceHt;
    this.discount = discount;
    this.tva = tva;
    this.unitEcoTax = unitEcoTax;
    this.informations = informations ?? [];
    this.composite = composite;
    this.accessories = accessories ?? [];
    this.orderNumber = orderNumber;
    this.supplierName = supplierName;
    this.supplierSent = supplierSent;
    this.supplierSentLastDate = supplierSentLastDate;
    this.automaticSupplierSent = automaticSupplierSent;
    this.index = index;
  }

  get unitPriceHtFull(): number {
    return this.unitPriceHt + this.unitEcoTax;
  }

  get unitPriceTtc(): number {
    return getPriceTtcFromHtAndTva(this.unitPriceHtFull, this.tva);
  }

  get unitPriceDiscountTtc(): number {
    // L'écotaxe ne peut pas être remisée, elle est donc exclue du calcul de la remise
    return getPriceTtcFromHtAndTva(
      getPriceWithDiscount(this.unitPriceHt, this.discount) + this.unitEcoTax,
      this.tva
    );
  }

  get unitEcoTaxTtc(): number {
    return getPriceTtcFromHtAndTva(this.unitEcoTax, this.tva);
  }

  get totalPriceAccessoryTtc(): number {
    let total = 0;
    this.accessories.forEach(a => {
      const unitHt = getPriceHtFromTtc(a.accessory.priceTtc, a.accessory.tva);
      total += getPriceTtcFromHtAndTva(unitHt, Number(a.tva)) * a.quantity;
    });
    return total;
  }

  get tvaAccessory(): number {
    let total = 0;
    this.accessories.forEach(a => {
      total += getTvaAmountFromPriceTtc(this.totalPriceAccessoryTtc, Number(a.tva));
    });
    return total;
  }

  get totalPriceTtc(): number {
    return this.unitPriceTtc * this.quantity;
  }

  get totalPriceDiscountTtc(): number {
    return this.unitPriceDiscountTtc * this.quantity;
  }

  get totalEcoTaxTtc(): number {
    return this.unitEcoTaxTtc * this.quantity;
  }

  get observation(): ShoppingCartProductInformation {
    return this.informations.find(i => i.informationType === InformationTypeEnum.CustomerObservation);
  }

  get options(): ShoppingCartProductInformation[] {
    return this.informations.filter(i => i.informationType === InformationTypeEnum.Option);
  }
}
